import { PageHeader } from '@ksyos/design-system';
import { useParams } from '@ksyos/router';
import { useEffect, useState } from 'react';
import { EpisodeCategory, ManagerAuthorization, ServiceCode } from '@common/enums/';
import { fetchJson } from '@frontend/store/fetch';
import { translateKey } from '@frontend/translate';
import { hasOneAuthorization } from '@frontend/utils';
import type { IServiceConsumer } from '../../../interfaces/serviceConsumer';
import { SettingsCategoryFilterButtonRow } from '../../../elements/SettingsCategoryFilterButtonRow';
import { usePersonalSettings } from '../PersonalSettingsContext';
import {
    hasMentalHealthServiceSettingComponent,
    loadMentalHealthServiceSettingCustomControl,
} from './GgzPersonalSettingsCustomControl';

export function GgzPersonalSettingsContainer() {
    const { isReadOnly } = usePersonalSettings();
    const { employmentId } = useParams() as { employmentId: string };
    const hasEmploymentSettingsReadAuthorization = hasOneAuthorization(ManagerAuthorization.employmentSettingsRead);

    const episodeCategory = EpisodeCategory.mentalHealth;

    useEffect(() => {
        loadServiceConsumers();
    }, [employmentId]);

    const [serviceConsumers, setServiceConsumers] = useState<IServiceConsumer[]>([]);
    const [selectedServiceConsumer, setSelectedServiceConsumer] = useState<IServiceConsumer>();

    return (
        <>
            <PageHeader title={renderH1()} />

            {serviceConsumers.length > 1 && !hasEmploymentSettingsReadAuthorization && renderMentalHealthTabs()}
            {selectedServiceConsumer && renderCustomControl(selectedServiceConsumer)}
        </>
    );

    function renderH1() {
        if (hasEmploymentSettingsReadAuthorization && episodeCategory === EpisodeCategory.mentalHealth) {
            return translateKey('episodeCategory.ggzContactInformation');
        }

        return serviceConsumers.length > 1
            ? translateKey(`episodeCategoryEnum.${episodeCategory}`)
            : selectedServiceConsumer?.service.name;
    }

    function renderMentalHealthTabs() {
        return (
            <SettingsCategoryFilterButtonRow
                collection={serviceConsumers.map((consumer) => ({ displayText: consumer.service.name, ...consumer }))}
                onClick={setSelectedServiceConsumer}
                selectedItem={selectedServiceConsumer}
            />
        );
    }

    function renderCustomControl(serviceConsumer: IServiceConsumer) {
        const MentalHealthSettingCustomControl = loadMentalHealthServiceSettingCustomControl(
            serviceConsumer.service.code!
        );

        return (
            <MentalHealthSettingCustomControl
                employmentId={employmentId}
                isReadOnly={isReadOnly}
                serviceConsumerId={serviceConsumer.id}
            />
        );
    }

    async function loadServiceConsumers() {
        const isActiveQueryParameter = getIsActiveQueryParameter();
        const result = await fetchJson(
            `/api/serviceConsumers?employmentId=${employmentId}&episodeCategory=${episodeCategory}${isActiveQueryParameter}`,
            'GET'
        );

        const consumers: IServiceConsumer[] = result.json.items.reduce(
            (filtered: IServiceConsumer[], consumer: IServiceConsumer) => {
                // Only display tabs/components for the consumers that have a settings page
                if (hasMentalHealthServiceSettingComponent(consumer.service.code!)) {
                    // If consumer of GGZ consult, it should always be the first tab and initially selected page
                    if (consumer.service.code === ServiceCode.GGZCt) {
                        return [consumer, ...filtered];
                    }

                    return [...filtered, consumer];
                }

                return filtered;
            },
            []
        );

        if (consumers.length > 0) {
            setSelectedServiceConsumer(consumers[0]);
        }
        setServiceConsumers(consumers);
    }

    function getIsActiveQueryParameter() {
        return isReadOnly ? '' : '&isActive=true';
    }
}
